// Canonical matching for the tenant missing-documents explanation note
// (TenantMissingDocNote).
//
// The note is written in two places — the sign-up documents step and the
// profile modal — and read in more (profile rows, header counts, reminder
// emails, forensic sections). Each surface names documents slightly
// differently ("1st month payslip" vs "Payslip month 1" vs "payslip -
// 9/2026"), so matching uses a canonical key instead of the raw strings.
//
// Granularity rule (the per-payslip edge case): every payslip slot is its
// own entry. Selecting "1st month payslip" must NOT select "2nd month
// payslip". Only a generic "Payslip" explanation (no slot info) covers all
// payslip slots. Everything else matches by document family.
// Backend mirror: controllers/tenantMissingDocNoteController.js
// (canonicalizeMissingDocKey). Keep both implementations in sync.

export const EXPLAINED_DOC_MAX_LENGTH = 1000;

// Canonical payslip tokens.
const GENERIC_PAYSLIP_KEY = "payslip:any";
const payslipSlotKey = (slot) => `payslip:slot:${slot}`;

// Detect a payslip slot number (1-3) in a document key. Both vocabularies
// are covered: "1st month payslip" (sign-up / profile) and "Payslip month 1"
// (forensic sections). Returns 0 when the key carries no slot info.
const detectPayslipSlot = (normalizedKey) => {
  const ordinalMatch = normalizedKey.match(/\b([123])(?:st|nd|rd)\b/);
  if (ordinalMatch) return Number(ordinalMatch[1]);
  const monthMatch = normalizedKey.match(/\bmonth\s*([123])\b/);
  if (monthMatch) return Number(monthMatch[1]);
  return 0;
};

const getRequiredPayslipPeriodKeys = () => {
  const now = new Date();
  const periodKeys = [];
  for (let i = 0; i < 3; i++) {
    let year = now.getFullYear();
    let month = now.getMonth() + 1 - i - 1;
    if (month <= 0) {
      month += 12;
      year -= 1;
    }
    periodKeys.push(`${month}/${year}`);
  }
  return periodKeys;
};

// Map any document key to its canonical matching key. Distinct payslip slots
// map to distinct canonicals; a generic payslip maps to one shared token.
export const canonicalizeMissingDocKey = (docKey) => {
  const normalized = String(docKey ?? "")
    .toLowerCase()
    .trim();
  if (!normalized) return "";
  if (normalized.includes("payslip")) {
    // Period-specific forensic keys ("payslip - 9/2026") resolve to the slot
    // covering that period; unknown periods stay matchable only by raw key.
    const periodMatch = normalized.match(/(\d{1,2})\/(\d{4})/);
    if (periodMatch) {
      const slotIndex = getRequiredPayslipPeriodKeys().indexOf(
        `${Number(periodMatch[1])}/${periodMatch[2]}`
      );
      return slotIndex >= 0
        ? payslipSlotKey(slotIndex + 1)
        : `payslip:period:${Number(periodMatch[1])}/${periodMatch[2]}`;
    }
    const slot = detectPayslipSlot(normalized);
    return slot >= 1 && slot <= 3 ? payslipSlotKey(slot) : GENERIC_PAYSLIP_KEY;
  }
  if (normalized.includes("model 100") || normalized.includes("model_100"))
    return "model_100";
  if (normalized.includes("model 130") || normalized.includes("model_130"))
    return "model_130";
  if (
    normalized.includes("303") ||
    normalized.includes("vat") ||
    normalized.includes("iva")
  ) {
    const quarterMatch = normalized.match(/q\s*([1-4])/);
    return quarterMatch ? `vat_q${quarterMatch[1]}` : "vat";
  }
  if (
    normalized.includes("working life") ||
    normalized.includes("vida laboral")
  )
    return "working_life";
  if (normalized.includes("deduction")) return "deduction_certificate";
  if (normalized.includes("employment contract")) return "employment_contract";
  if (normalized.includes("appointment") || normalized.includes("gazette"))
    return "appointment_notice";
  if (normalized.includes("revaloriz")) return "revalorization_letter";
  if (normalized.includes("benefit") || normalized.includes("comprehensive"))
    return "benefits_certificate";
  if (normalized.includes("balance sheet")) return "corporate_balance_sheet";
  if (
    normalized.includes("p&l") ||
    normalized.includes("pnl") ||
    (normalized.includes("profit") && normalized.includes("loss"))
  )
    return "corporate_pnl";
  if (normalized.includes("corporate tax")) return "corporate_tax_return";
  if (
    normalized.includes("incorporation") ||
    normalized.includes("constitution")
  )
    return "incorporation_documents";
  if (normalized.includes("acceptance letter")) return "acceptance_letter";
  if (normalized.includes("registration contract"))
    return "registration_contract";
  if (normalized.includes("annual payment")) return "annual_payment";
  if (normalized.includes("residency")) return "residency_registration";
  if (normalized.includes("empadronamiento")) return "residency_registration";
  return normalized;
};

// Identification documents (passport, DNI/NIE, ...) are verified through
// Onfido, not through uploads — never explainable, never listed in the
// missing-documents explanation modal. `identityNames` is the caller-supplied
// source of truth (category config + required-docs map), so no document
// names are hardcoded here. Matching is token based across vocabularies:
// each candidate name is split on "/" into alternatives ("DNI/NIE" → "dni",
// "nie") and an alternative matches when all of its tokens appear in the key,
// so "national_identity_card_front" and "National identity card" both match
// while housing documents like "residency registration" (empadronamiento)
// and consent forms like "Client Identification Form" do not.
const normalizeDocNameTokens = (name) =>
  String(name ?? "")
    .toLowerCase()
    .split(/[^a-z]+/)
    .filter(Boolean);
export const isIdentityDocumentKey = (docKey, identityNames = []) => {
  const keyTokens = normalizeDocNameTokens(docKey);
  if (keyTokens.length === 0) return false;
  return (Array.isArray(identityNames) ? identityNames : []).some((name) => {
    const alternatives = String(name ?? "")
      .split("/")
      .map(normalizeDocNameTokens)
      .filter((tokens) => tokens.length > 0);
    return alternatives.some((nameTokens) =>
      nameTokens.every((token) => keyTokens.includes(token))
    );
  });
};
// Migrate a stored documentFamily from the first version of the note (plain
// family keys like "payslip") to the canonical scheme.
const migrateStoredCanonicalKey = (storedFamily) => {
  if (!storedFamily) return "";
  if (storedFamily === "payslip") return GENERIC_PAYSLIP_KEY;
  return String(storedFamily);
};

// Canonical-key set of the documents covered by a note. Built from the raw
// keys (recomputed, so notes written before the canonical scheme still
// match) plus any stored canonicals.
export const getExplainedDocCanonicalKeys = (missingDocNote) => {
  const canonicalKeys = new Set();
  for (const doc of missingDocNote?.explainedDocuments ?? []) {
    const fromKey = canonicalizeMissingDocKey(doc?.documentKey ?? doc?.key);
    if (fromKey) canonicalKeys.add(fromKey);
    const fromStored = migrateStoredCanonicalKey(doc?.documentFamily);
    if (fromStored) canonicalKeys.add(fromStored);
  }
  return canonicalKeys;
};

// True when a canonical set covers a document key: exact canonical match,
// or a generic payslip explanation covering any payslip slot. A single slot
// never covers another slot.
export const isCanonicalKeyCovered = (canonicalKeys, docKey) => {
  if (!canonicalKeys || canonicalKeys.size === 0) return false;
  const canonical = canonicalizeMissingDocKey(docKey);
  if (!canonical) return false;
  if (canonicalKeys.has(canonical)) return true;
  return (
    canonical.startsWith("payslip:slot:") &&
    canonicalKeys.has(GENERIC_PAYSLIP_KEY)
  );
};

// True when the given document key is covered by the tenant's explanation
// note. Used to swap the missing warning for a "View note" button and to
// exclude the document from "still to upload" counts and reminder text.
// The document itself stays missing everywhere else.
export const isMissingDocExplained = (docKey, missingDocNote) => {
  if (!missingDocNote) return false;
  return isCanonicalKeyCovered(
    getExplainedDocCanonicalKeys(missingDocNote),
    docKey
  );
};

// Remove explained documents from a missing-docs list (canonical matching,
// so sign-up, profile, and forensic vocabularies match per slot). The full
// missing list (score/completion) is left untouched — only display counts
// and reminder text use the filtered list.
export const filterUnexplainedMissingDocs = (missingDocs, missingDocNote) => {
  if (!Array.isArray(missingDocs) || missingDocs.length === 0) return [];
  if (!missingDocNote) return [...missingDocs];
  const explainedKeys = getExplainedDocCanonicalKeys(missingDocNote);
  if (explainedKeys.size === 0) return [...missingDocs];
  return missingDocs.filter(
    (docKey) => !isCanonicalKeyCovered(explainedKeys, docKey)
  );
};
